package dairy.mynotes.reusablecomponents

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.KeyboardArrowDown
import androidx.compose.material.icons.filled.KeyboardArrowUp
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import dairy.mynotes.ui.theme.AppColors

/**
 * JS/Web implementation of CategoryDropdown.
 * Uses a plain Column with AnimatedVisibility list — avoids DropdownMenu
 * which has portal/overlay rendering issues on Compose for Web.
 */
@Composable
actual fun CategoryDropdown(
    categories: List<String>,
    selectedCategory: String,
    onCategorySelected: (String) -> Unit,
    modifier: Modifier
) {
    var expanded by remember { mutableStateOf(false) }
    val shape = RoundedCornerShape(10.dp)
    val displayValue = if (selectedCategory.isNotEmpty() && categories.contains(selectedCategory))
        selectedCategory else "Select a category"

    Column(modifier = modifier.fillMaxWidth()) {
        // Label
        Text(
            text = "Categories",
            fontSize = 12.sp,
            color = AppColors.TextSecondary,
            modifier = Modifier.padding(bottom = 4.dp)
        )

        // Clickable header row styled like an OutlinedTextField
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .clip(shape)
                .border(
                    width = 1.dp,
                    color = if (expanded) AppColors.Primary else AppColors.LightGray,
                    shape = shape
                )
                .background(Color.White)
                .clickable { expanded = !expanded }
                .padding(horizontal = 16.dp, vertical = 14.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = displayValue,
                fontSize = 16.sp,
                color = if (selectedCategory.isNotEmpty() && categories.contains(selectedCategory))
                    AppColors.TextPrimary else AppColors.TextSecondary
            )
            Icon(
                imageVector = if (expanded) Icons.Filled.KeyboardArrowUp else Icons.Filled.KeyboardArrowDown,
                contentDescription = if (expanded) "Collapse" else "Expand",
                tint = AppColors.TextSecondary,
                modifier = Modifier.size(24.dp)
            )
        }

        // Inline dropdown list — renders in normal flow, no portal issues
        AnimatedVisibility(visible = expanded) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .border(
                        width = 1.dp,
                        color = AppColors.LightGray,
                        shape = RoundedCornerShape(bottomStart = 10.dp, bottomEnd = 10.dp)
                    )
                    .background(Color.White)
            ) {
                categories.forEachIndexed { index, category ->
                    if (index > 0) {
                        HorizontalDivider(color = AppColors.LightGray)
                    }
                    Text(
                        text = category,
                        fontSize = 16.sp,
                        color = AppColors.TextPrimary,
                        modifier = Modifier
                            .fillMaxWidth()
                            .clickable {
                                onCategorySelected(category)
                                expanded = false
                            }
                            .padding(horizontal = 16.dp, vertical = 12.dp)
                    )
                }
            }
        }
    }
}

